import { ISettings } from '@admin/pages/settings-manager/interfaces/settings.interface';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, Inject, OnInit, Optional, PLATFORM_ID, Renderer2 } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AnalyticsService } from '@core/services/analytics.service';
import { AuthService } from '@core/services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '@shared/services/language.service';
import { MessageService } from '@shared/services/message.service';
import { PlatformService } from '@shared/services/platform-service.service';
import { PublicGenericService } from '@shared/services/public-generic.service';
import { SearchService } from '@shared/services/search.service';
import { SEOService } from '@shared/services/seo.service';
import { SSR_ACCEPT_LANGUAGE, SSR_PATH } from '@shared/tokens/server.token';
import { environment } from 'src/environments/environment';
import { LoginService } from './login/services/login.service';
import { UserService } from './users-manager/services/user.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit {
  appSettings: ISettings;
  isLoaded: boolean = false;
  showHeader: boolean = true;
  showFooter: boolean = true;
  showAllFooter: boolean = true;
  withLoader: boolean = true;

  constructor(
    translate: TranslateService,
    public loginService: LoginService,
    public authService: AuthService,
    public userService: UserService,
    private languageService: LanguageService,
    private searchService: SearchService,
    private publicGenericService: PublicGenericService,
    private router: Router,
    private messageService: MessageService,
    @Inject(PLATFORM_ID) private _platformId: Object,
    @Optional() @Inject(SSR_PATH) private ssrPath: string,
    @Optional() @Inject(SSR_ACCEPT_LANGUAGE) private ssrLang: string,
    @Inject(DOCUMENT) private document: Document,
    private _renderer: Renderer2,
    private seoService: SEOService,
    private analyticsService: AnalyticsService,
    private platformService: PlatformService
  ) {
    const lang = ssrLang ?? (ssrPath ?? globalThis.window?.location?.pathname)?.split('/')[1];
    const langSpecified = lang === 'it' || lang === 'en';
    const defaultLanguage = lang === 'it' ? 'it' : 'en';
    this.document.documentElement.lang = defaultLanguage;

    this.router.events.subscribe(event => {
      if(event instanceof NavigationEnd){
          this.platformService.runOnBrowser(() => {
            this.hideHeaderFooter(event);
          })
          this.seoService.updateCanonicalUrl(environment.canonicalUrl+event.url.slice(1)
            , {
              it: environment.canonicalUrl+event.url.slice(1).replace('/en/','/it/'),
              en: environment.canonicalUrl+event.url.slice(1).replace('/it/','/en/')
            }
        );
       }

    });

    this.platformService.runOnServer(() => {

      translate.setDefaultLang(defaultLanguage);
      translate.use(defaultLanguage);
      this.languageService.userSelectedLanguage = {
        idLanguage: 1,
        code: defaultLanguage,
        description: ""
      };
    })

    this.platformService.runOnBrowser(() => {

      const langUser = window.navigator.language?.toLowerCase() ?? 'en';
      this.withLoader = !location.pathname.includes('/event/');

      if (localStorage && ((localStorage.getItem('langId') != null) && (localStorage.getItem('langCode') != null))) {
        const savedLang = localStorage.getItem('langCode');
        let toSetLang = savedLang;
        if(savedLang !== defaultLanguage && langSpecified) {
          localStorage.setItem('langCode', defaultLanguage);
          localStorage.setItem('langId', defaultLanguage === 'it' ? '1' : '2');
          toSetLang = defaultLanguage;
        }

        translate.setDefaultLang(toSetLang);
        translate.use(toSetLang);
        this.languageService.userSelectedLanguage = {
          idLanguage: parseInt(localStorage.getItem('langId')),
          code: toSetLang,
          description: ""
        };
      } else {
        if (defaultLanguage === 'it' || langUser.startsWith('it')) {
          translate.setDefaultLang('it');
          translate.use('it');
          this.languageService.userSelectedLanguage = {
            idLanguage: 1,
            code: 'it',
            description: ""
          };
        } else {
          translate.setDefaultLang('en');
          translate.use('en');
          this.languageService.userSelectedLanguage = {
            idLanguage: 2,
            code: 'en',
            description: ""
          };
        }
      }
   })
  }

  ngOnInit(): void {

    this.languageService.fillLanguages();

    this.isLoaded = !!this.appSettings

    this.publicGenericService.getGlobalSettings().subscribe((dataR) => {
      this.appSettings = dataR;
      this.isLoaded = true;
    });

    if(!this.authService.isPingAlreadyExecuted) {
      this.authService.isPingAlreadyExecuted = true;
      this.loginService.ping().subscribe((responseBody) => {
        if(responseBody == null) return;

        const username = responseBody.username;
        if (this.authService.userLogged.username === '') {
          this.userService.getByUsername(username).subscribe((response) => {
            this.authService.authResult = 1;
            this.authService.userLogged = response;
            this.messageService.startTimer();

          this.analyticsService.trackInit({
            idUser: this.authService.userLogged.idUser,
            email: this.authService.userLogged.username,
            firstName: this.authService.userLogged.entityFullName.split(' ')[0],
            lastName: this.authService.userLogged.entityFullName.split(' ').slice(1).join(' ')
          });
          });
        } else if(this.authService.isAuthenticated()) {
          this.messageService.startTimer();
        }
      });
    }

    if (isPlatformBrowser(this._platformId)) {
      this.searchService.filters.idLanguage = this.languageService.userSelectedLanguage.idLanguage;
      this.searchService.getAllTypes().subscribe((dataR) => {
        this.searchService.auctionTypeList = dataR;
      });
      this.publicGenericService.getAllDetails();
      this.publicGenericService.getAllCountries();
      this.publicGenericService.getPriceRange();
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this._platformId)) {
      let loader = this._renderer.selectRootElement('#loader');
      if (loader.style.display != "none") loader.style.display = "none";
    }
  }

  hideHeaderFooter(location) {
    if (location.url.includes('/event/')) {
      this.showHeader = false;
      this.showFooter = false;
      this.showAllFooter = false;
    } else if (location.url.includes('/eventnow/')) {
      this.showHeader = false;
      this.showFooter = true;
      this.showAllFooter = false;
    } else {
      this.showHeader = true;
      this.showFooter = true;
      this.showAllFooter = true;
    }
  }
}
